import { GetPayUpType, GetPayUpsResponseType, PostPayUpType } from '@orbit/api-types'
import { getDeviceContext } from '@lib/analytics'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { nowUnix as currentUnix } from '@lib/datetime'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface CreatePayUpVariables {
    tripId: string
    payUpData: PostPayUpType
    currentUserId: number
}

interface CreatePayUpContext {
    snapshot: GetPayUpsResponseType | undefined
    tempId: number
}

export const useCreatePayUp = () => {
    const queryClient = useQueryClient()

    return useMutation<GetPayUpType, Error, CreatePayUpVariables, CreatePayUpContext>({
        mutationFn: async ({ tripId, payUpData }) => {
            const response = await api.post<GetPayUpType>(`/trips/${tripId}/payUps`, { ...payUpData, analytics: getDeviceContext() })
            return response.data
        },
        onMutate: async ({ tripId, payUpData, currentUserId }) => {
            const queryKey = queryKeys.payups(tripId)
            await queryClient.cancelQueries({ queryKey })
            const snapshot = queryClient.getQueryData<GetPayUpsResponseType>(queryKey)
            const tempId = -Date.now()
            const nowUnix = currentUnix()
            const tempPayUp: GetPayUpType = {
                id: tempId,
                tripId: Number(tripId),
                fromUserId: payUpData.fromUserId,
                toUserId: payUpData.toUserId,
                amount: payUpData.amount,
                currencyCode: payUpData.currencyCode ?? 'usd',
                payUpDate: nowUnix,
                timestamp: nowUnix,
                lastUpdatedAt: nowUnix,
                createdByUserId: currentUserId,
                lastUpdatedByUserId: currentUserId,
                fileIds: [],
            }
            queryClient.setQueryData<GetPayUpsResponseType>(queryKey, prev => ({
                payUps: [tempPayUp, ...(prev?.payUps ?? [])],
            }))
            return { snapshot, tempId }
        },
        onError: (_err, variables, context) => {
            if (context) {
                queryClient.setQueryData(queryKeys.payups(variables.tripId), context.snapshot)
            }
        },
        onSettled: (_data, _err, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payups(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.paymentsOwed(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.expenses(variables.tripId) })
        },
    })
}
